import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CheckSquare,
  FileText,
  GripVertical,
  Handshake,
  Hash,
  Info,
  LayoutGrid,
  List,
  Lock,
  MessageCircle,
  Cpu,
  Sparkles,
  MessagesSquare,
  MoreHorizontal,
  Plus,
  Search,
  Settings,
  Shield,
  ToggleLeft,
  Type,
  Users,
  Video,
} from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../components/ui/dropdown-menu";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { Switch } from "../../../../../components/ui/switch";
import { Textarea } from "../../../../../components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";
import {
  queryClient,
  useCreateObjectField,
  useUpdateWorkspaceField,
  useUpdateWorkspaceObject,
} from "../../../../../hooks/QueryHooks";
import { cn } from "../../../../../lib/utils";
import { getObjectFields } from "../../../../../services/workspace/workspaceEndpoints";
import { toast } from "sonner";
import type { FieldWithMappings, ObjectWithIntegrations } from "../types";

const ICON_STYLE_BY_SLUG: Record<string, { icon: React.ElementType; color: string }> = {
  accounts: { icon: Building2, color: "bg-blue-500" },
  companies: { icon: Building2, color: "bg-blue-500" },
  users: { icon: Users, color: "bg-green-500" },
  contacts: { icon: Users, color: "bg-green-500" },
  people: { icon: Users, color: "bg-emerald-500" },
  deals: { icon: Handshake, color: "bg-orange-500" },
  meetings: { icon: Calendar, color: "bg-purple-500" },
  tasks: { icon: CheckSquare, color: "bg-muted-foreground" },
  notes: { icon: FileText, color: "bg-blue-500" },
  conversation_messages: { icon: MessageCircle, color: "bg-green-500" },
  conversations: { icon: MessagesSquare, color: "bg-cyan-500" },
  meeting_recordings: { icon: Video, color: "bg-violet-500" },
};

const defaultIconStyle = { icon: Building2, color: "bg-muted-foreground" };

const FIELD_TYPE_ICONS: Record<string, React.ElementType> = {
  string: Type,
  text: Type,
  integer: Hash,
  number: Hash,
  float: Hash,
  decimal: Hash,
  boolean: ToggleLeft,
  date: Calendar,
  datetime: Calendar,
  "multi-select": List,
  relationship: Users,
};

const getFieldTypeIcon = (dataType: string): React.ElementType =>
  FIELD_TYPE_ICONS[dataType.toLowerCase()] || FileText;

const FIELD_TYPE_OPTIONS = [
  { value: "string", label: "Text", lucideIcon: Type },
  { value: "int", label: "Integer", lucideIcon: Hash },
  { value: "float", label: "Float", lucideIcon: Hash },
  { value: "boolean", label: "Boolean", lucideIcon: ToggleLeft },
  { value: "datetime", label: "DateTime", lucideIcon: Calendar },
  { value: "list", label: "List", lucideIcon: List },
];

type DetailTab = "configuration" | "permissions" | "attributes";

type EditObjectDraft = {
  plural_noun: string;
  singular_noun: string;
  description: string;
  enabled: boolean;
};

type EditFieldDraft = {
  display_name: string;
  description: string;
  is_hidden: boolean;
  is_updateable: boolean;
};

type AddFieldDraft = {
  display_name: string;
  data_type: string;
  description: string;
};

type ObjectDetailViewProps = {
  object: ObjectWithIntegrations & { record_count?: number | null; records_count?: number | null };
  workspaceId: string;
  onBack: () => void;
};

const ObjectDetailView = ({ object, workspaceId, onBack }: ObjectDetailViewProps) => {
  const [activeTab, setActiveTab] = useState<DetailTab>("configuration");
  const [fieldSearch, setFieldSearch] = useState("");

  // Edit object modal
  const [editObjectOpen, setEditObjectOpen] = useState(false);
  const [savingObject, setSavingObject] = useState(false);
  const [editObjectDraft, setEditObjectDraft] = useState<EditObjectDraft>({
    plural_noun: object.plural_noun || "",
    singular_noun: object.singular_noun || "",
    description: object.description || "",
    enabled: Boolean(object.enabled),
  });

  // Edit field modal
  const [editFieldOpen, setEditFieldOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<FieldWithMappings | null>(null);
  const [editFieldDraft, setEditFieldDraft] = useState<EditFieldDraft>({
    display_name: "",
    description: "",
    is_hidden: false,
    is_updateable: true,
  });

  // Add field modal
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [savingField, setSavingField] = useState(false);
  const [addFieldDraft, setAddFieldDraft] = useState<AddFieldDraft>({
    display_name: "",
    data_type: "string",
    description: "",
  });

  const updateObjectMutation = useUpdateWorkspaceObject();
  const updateFieldMutation = useUpdateWorkspaceField();
  const createFieldMutation = useCreateObjectField();

  const { data: fieldsResponse, isLoading: fieldsLoading } = useQuery({
    queryKey: ["object-detail-fields", workspaceId, object.id],
    queryFn: () => getObjectFields({ workspace_id: workspaceId, object_id: object.id }),
    enabled: !!workspaceId && !!object.id,
    staleTime: 1000 * 60 * 5,
  });

  const fields = useMemo<FieldWithMappings[]>(() => {
    const rows = Array.isArray(fieldsResponse?.data) ? fieldsResponse!.data : [];
    return rows.map((field: any) => ({
      ...field,
      object: { id: object.id, api_slug: object.api_slug, name: object.plural_noun },
      has_external_mappings: false,
      external_mappings: [],
    }));
  }, [fieldsResponse, object]);

  const filteredFields = useMemo(() => {
    const q = fieldSearch.trim().toLowerCase();
    if (!q) return fields;
    return fields.filter(
      (f) =>
        f.display_name.toLowerCase().includes(q) ||
        f.column_name.toLowerCase().includes(q) ||
        (f.description || "").toLowerCase().includes(q),
    );
  }, [fields, fieldSearch]);

  const iconConfig = ICON_STYLE_BY_SLUG[object.api_slug] || defaultIconStyle;
  const Icon = iconConfig.icon;

  // --- Edit Object ---
  const handleOpenEditObject = () => {
    setEditObjectDraft({
      plural_noun: object.plural_noun || "",
      singular_noun: object.singular_noun || "",
      description: object.description || "",
      enabled: Boolean(object.enabled),
    });
    setEditObjectOpen(true);
  };

  const handleSaveObject = async () => {
    if (object.is_system_object) {
      setEditObjectOpen(false);
      return;
    }
    if (editObjectDraft.plural_noun.trim().length < 2 || editObjectDraft.singular_noun.trim().length < 2) {
      toast.error("Both singular and plural names must be at least 2 characters.");
      return;
    }
    setSavingObject(true);
    try {
      await updateObjectMutation.mutateAsync({
        workspace_id: workspaceId,
        object_id: object.id,
        data: {
          plural_noun: editObjectDraft.plural_noun.trim(),
          singular_noun: editObjectDraft.singular_noun.trim(),
          description: editObjectDraft.description.trim() || null,
          enabled: editObjectDraft.enabled,
        },
      });
      toast.success("Object updated successfully.");
      setEditObjectOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["workspace-objects-detailed", workspaceId] });
    } catch {
      toast.error("Unable to update object. Please try again later.");
    } finally {
      setSavingObject(false);
    }
  };

  // --- Edit Field ---
  const handleOpenEditField = (field: FieldWithMappings) => {
    setSelectedField(field);
    setEditFieldDraft({
      display_name: field.display_name || "",
      description: field.description || "",
      is_hidden: Boolean(field.is_hidden),
      is_updateable: Boolean(field.is_updateable),
    });
    setEditFieldOpen(true);
  };

  const handleSaveField = async () => {
    if (!selectedField) return;
    if (selectedField.is_protected) {
      toast.info("Protected fields cannot be edited.");
      return;
    }
    if (editFieldDraft.display_name.trim().length < 2) {
      toast.error("Display name must be at least 2 characters.");
      return;
    }
    try {
      await updateFieldMutation.mutateAsync({
        workspace_id: workspaceId,
        field_id: selectedField.id,
        data: {
          display_name: editFieldDraft.display_name.trim(),
          description: editFieldDraft.description.trim() || "",
          is_hidden: editFieldDraft.is_hidden,
          is_updateable: editFieldDraft.is_updateable,
        },
      });
      toast.success("Field updated successfully.");
      setEditFieldOpen(false);
      setSelectedField(null);
      await queryClient.invalidateQueries({ queryKey: ["object-detail-fields", workspaceId, object.id] });
    } catch {
      toast.error("Could not update field settings.");
    }
  };

  // --- Add Field ---
  const handleOpenAddField = () => {
    setAddFieldDraft({ display_name: "", data_type: "string", description: "" });
    setAddFieldOpen(true);
  };

  const handleSaveAddField = async () => {
    if (addFieldDraft.display_name.trim().length < 2) {
      toast.error("Display name must be at least 2 characters.");
      return;
    }
    const columnName = addFieldDraft.display_name.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    if (!columnName) {
      toast.error("Could not generate a valid column name.");
      return;
    }
    setSavingField(true);
    try {
      await createFieldMutation.mutateAsync({
        workspace_id: workspaceId,
        object_id: object.id,
        data: {
          display_name: addFieldDraft.display_name.trim(),
          column_name: columnName,
          data_type: addFieldDraft.data_type,
          description: addFieldDraft.description.trim() || "",
          source: "native",
        },
      });
      toast.success("Attribute created successfully.");
      setAddFieldOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["object-detail-fields", workspaceId, object.id] });
      await queryClient.invalidateQueries({ queryKey: ["workspace-objects-detailed", workspaceId] });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "Could not create attribute.";
      toast.error(message);
    } finally {
      setSavingField(false);
    }
  };

  const tabs: { value: DetailTab; label: string; icon: React.ElementType; disabled?: boolean }[] = [
    { value: "configuration", label: "Configuration", icon: Settings },
    { value: "permissions", label: "Permissions", icon: Shield, disabled: true },
    { value: "attributes", label: "Attributes", icon: LayoutGrid },
  ];

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl text-white", iconConfig.color)}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-[18px] font-semibold text-foreground">{object.plural_noun}</h1>
              <span className="rounded bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {object.is_system_object ? "Standard" : "Custom"}
              </span>
            </div>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              Manage object attributes and other relevant settings
            </p>
          </div>
        </div>
        <Button variant="outline" className="h-9 text-[13px]" onClick={handleOpenEditObject}>
          Edit Object
        </Button>
      </div>

      {/* Underline tabs */}
      <div className="flex items-center gap-0 border-b border-border">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.value}
              type="button"
              disabled={tab.disabled}
              onClick={() => !tab.disabled && setActiveTab(tab.value)}
              className={cn(
                "flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-medium transition-colors",
                activeTab === tab.value
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
                tab.disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <TabIcon className="h-3.5 w-3.5" />
              {tab.label}
              {tab.value === "attributes" && (
                <span className="ml-0.5 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                  {fields.length}
                </span>
              )}
              {tab.disabled && (
                <span className="ml-0.5 rounded bg-muted px-1 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Configuration Tab */}
      {activeTab === "configuration" && (
        <div className="space-y-6">
          <div>
            <h3 className="text-[14px] font-semibold text-foreground">General</h3>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              Set words to describe a single and multiple objects of this type
            </p>
          </div>

          {/* Side-by-side singular/plural */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <Label className="text-[12px] text-muted-foreground">Singular noun</Label>
              <div className="flex items-center gap-2">
                <div className={cn("flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-white", iconConfig.color)}>
                  <Icon className="h-4 w-4" />
                </div>
                <Input
                  value={object.singular_noun || ""}
                  disabled
                  className="h-9 text-[13px]"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] text-muted-foreground">Plural noun</Label>
              <Input
                value={object.plural_noun || ""}
                disabled
                className="h-9 text-[13px]"
              />
            </div>
          </div>

          {/* Slug field with "/" prefix */}
          <div className="space-y-1.5 pt-2">
            <div className="flex items-center gap-1.5">
              <Label className="text-[12px] text-muted-foreground">Identifier / Slug</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-[12px]">The unique identifier used in the API</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] text-muted-foreground">/</span>
              <Input
                value={object.api_slug}
                disabled
                className="h-9 font-mono text-[13px]"
              />
            </div>
            {object.is_system_object && (
              <p className="text-[11px] text-muted-foreground">You can't change the slug for standard objects</p>
            )}
          </div>
        </div>
      )}

      {/* Attributes Tab */}
      {activeTab === "attributes" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative min-w-[240px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search attributes..."
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
                className="h-8 pl-8 text-[13px]"
              />
            </div>
            <Button size="sm" className="h-8 gap-1.5 text-[12px]" onClick={handleOpenAddField}>
              <Plus className="h-3.5 w-3.5" />
              Create attribute
            </Button>
          </div>

          {/* Grid-based field table */}
          <div className="overflow-hidden rounded-lg border border-border">
            {/* Header */}
            <div className="grid grid-cols-[24px_1fr_120px_120px_1fr_32px] gap-2 border-b border-border bg-muted/50 px-4 py-2.5 text-[11px] font-medium text-muted-foreground">
              <span />
              <div className="flex items-center gap-1.5">
                <Settings className="h-3 w-3" />
                Name
              </div>
              <div className="flex items-center gap-1.5">
                <FileText className="h-3 w-3" />
                Type
              </div>
              <div className="flex items-center gap-1.5">
                <Shield className="h-3 w-3" />
                Constraints
              </div>
              <div className="flex items-center gap-1.5">
                <LayoutGrid className="h-3 w-3" />
                Properties
              </div>
              <span />
            </div>

            {/* Rows */}
            {fieldsLoading ? (
              <div className="px-4 py-12 text-center text-[13px] text-muted-foreground">
                Loading attributes...
              </div>
            ) : filteredFields.length > 0 ? (
              <div className="divide-y divide-border">
                {filteredFields.map((field) => {
                  const FieldIcon = getFieldTypeIcon(field.data_type);
                  const generationType = (field as any).generation_type as string | undefined;
                  const isComputed = generationType === "computed_trait";
                  const isAIGenerated = generationType === "ai_generated";

                  return (
                    <div
                      key={field.id}
                      className="group grid cursor-pointer grid-cols-[24px_1fr_120px_120px_1fr_32px] items-center gap-2 px-4 py-2.5 transition-colors hover:bg-muted/50"
                      onClick={() => handleOpenEditField(field)}
                    >
                      {/* Drag handle */}
                      <div className="text-muted-foreground/30 transition-colors group-hover:text-muted-foreground">
                        <GripVertical className="h-3.5 w-3.5" />
                      </div>

                      {/* Name */}
                      <div className="flex min-w-0 items-center gap-2">
                        <FieldIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        <span className="truncate text-[13px] font-medium text-foreground">
                          {field.display_name}
                        </span>
                      </div>

                      {/* Type */}
                      <span className="text-[13px] text-muted-foreground">{field.data_type}</span>

                      {/* Constraints */}
                      <span className="text-[13px] text-muted-foreground">
                        {field.is_protected ? "Protected" : !field.is_updateable ? "Read-only" : ""}
                      </span>

                      {/* Properties */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        {field.is_system_field && (
                          <span className="flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                            <Settings className="h-2.5 w-2.5" />
                            System
                          </span>
                        )}
                        {isComputed && (
                          <span className="flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">
                            <Cpu className="h-2.5 w-2.5" />
                            Computed
                          </span>
                        )}
                        {isAIGenerated && (
                          <span className="flex items-center gap-1 rounded bg-violet-100 px-1.5 py-0.5 text-[11px] text-violet-700">
                            <Sparkles className="h-2.5 w-2.5" />
                            AI Generated
                          </span>
                        )}
                        {field.is_hidden && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                            Hidden
                          </span>
                        )}
                      </div>

                      {/* More menu */}
                      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground opacity-0 transition-all hover:bg-muted group-hover:opacity-100"
                            >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem onClick={() => handleOpenEditField(field)}>
                              Edit attribute
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-4 py-12 text-center text-[13px] text-muted-foreground">
                No attributes found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Object Modal */}
      <Dialog open={editObjectOpen} onOpenChange={setEditObjectOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold">
              Edit object: {object.plural_noun}
            </DialogTitle>
            <DialogDescription className="text-[12px]">
              Update basic object settings. API slug cannot be changed after creation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-[13px]">Display Name (Plural)</Label>
              <Input
                value={editObjectDraft.plural_noun}
                onChange={(e) => setEditObjectDraft((prev) => ({ ...prev, plural_noun: e.target.value }))}
                disabled={object.is_system_object}
                className="h-9 text-[13px]"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[13px]">Display Name (Singular)</Label>
              <Input
                value={editObjectDraft.singular_noun}
                onChange={(e) => setEditObjectDraft((prev) => ({ ...prev, singular_noun: e.target.value }))}
                disabled={object.is_system_object}
                className="h-9 text-[13px]"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[13px]">Description</Label>
              <Textarea
                value={editObjectDraft.description}
                onChange={(e) => setEditObjectDraft((prev) => ({ ...prev, description: e.target.value }))}
                disabled={object.is_system_object}
                className="min-h-[90px] text-[13px]"
                placeholder="Describe what this object represents"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[13px]">API Slug</Label>
              <Input value={object.api_slug} disabled className="h-9 font-mono text-[13px]" />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2.5">
              <div>
                <p className="text-[13px] text-foreground">Enabled</p>
                <p className="text-[11px] text-muted-foreground">Allow this object in lists and filters</p>
              </div>
              <Switch
                checked={editObjectDraft.enabled}
                onCheckedChange={(checked) => setEditObjectDraft((prev) => ({ ...prev, enabled: checked }))}
                disabled={object.is_system_object}
              />
            </div>

            {object.is_system_object && (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-[12px] text-blue-700">
                This is a system object. Some settings cannot be modified.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" className="text-[12px]" onClick={() => setEditObjectOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="text-[12px]"
              onClick={() => { handleSaveObject().catch(() => {}); }}
              disabled={savingObject || object.is_system_object}
            >
              {savingObject ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Field Modal */}
      <Dialog open={editFieldOpen} onOpenChange={setEditFieldOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold">
              Edit attribute{selectedField ? `: ${selectedField.display_name}` : ""}
            </DialogTitle>
            <DialogDescription className="text-[12px]">
              Update visibility and display settings for this attribute.
            </DialogDescription>
          </DialogHeader>

          {selectedField && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-[13px]">Display Name</Label>
                <Input
                  value={editFieldDraft.display_name}
                  onChange={(e) => setEditFieldDraft((prev) => ({ ...prev, display_name: e.target.value }))}
                  disabled={selectedField.is_protected}
                  className="h-9 text-[13px]"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[13px]">Description</Label>
                <Textarea
                  value={editFieldDraft.description}
                  onChange={(e) => setEditFieldDraft((prev) => ({ ...prev, description: e.target.value }))}
                  disabled={selectedField.is_protected}
                  className="min-h-[90px] text-[13px]"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[13px]">Column Name</Label>
                <Input value={selectedField.column_name} disabled className="h-9 font-mono text-[13px]" />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                  {selectedField.data_type}
                </span>
                <span className="rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                  {selectedField.source}
                </span>
                {selectedField.is_system_field && (
                  <span className="rounded border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    System
                  </span>
                )}
              </div>

              <div className="rounded-md border border-border bg-muted/40 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] text-foreground">Hidden from UI</p>
                    <p className="text-[11px] text-muted-foreground">Hide this field from user interfaces</p>
                  </div>
                  <Switch
                    checked={editFieldDraft.is_hidden}
                    onCheckedChange={(checked) => setEditFieldDraft((prev) => ({ ...prev, is_hidden: checked }))}
                    disabled={selectedField.is_protected}
                  />
                </div>
              </div>

              <div className="rounded-md border border-border bg-muted/40 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] text-foreground">Updateable</p>
                    <p className="text-[11px] text-muted-foreground">Allow updates via API</p>
                  </div>
                  <Switch
                    checked={editFieldDraft.is_updateable}
                    onCheckedChange={(checked) => setEditFieldDraft((prev) => ({ ...prev, is_updateable: checked }))}
                    disabled={selectedField.is_protected || selectedField.is_system_field}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" className="text-[12px]" onClick={() => setEditFieldOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="text-[12px]"
              onClick={() => { handleSaveField().catch(() => {}); }}
              disabled={!selectedField || selectedField.is_protected || updateFieldMutation.isLoading}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Field Modal */}
      <Dialog open={addFieldOpen} onOpenChange={setAddFieldOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-[16px] font-semibold">Create attribute</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Attribute Type</Label>
              <Select
                value={addFieldDraft.data_type}
                onValueChange={(val) => setAddFieldDraft((prev) => ({ ...prev, data_type: val }))}
              >
                <SelectTrigger className="h-10 text-[13px]">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      {(() => {
                        const opt = FIELD_TYPE_OPTIONS.find((o) => o.value === addFieldDraft.data_type);
                        const TypeIcon = opt?.lucideIcon || Type;
                        return (
                          <>
                            <TypeIcon className="h-4 w-4 text-muted-foreground" />
                            {opt?.label || addFieldDraft.data_type}
                          </>
                        );
                      })()}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPE_OPTIONS.map((opt) => {
                    const OptIcon = opt.lucideIcon;
                    return (
                      <SelectItem key={opt.value} value={opt.value} className="text-[13px]">
                        <span className="flex items-center gap-2">
                          <OptIcon className="h-4 w-4 text-muted-foreground" />
                          {opt.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Display Name</Label>
              <Input
                value={addFieldDraft.display_name}
                onChange={(e) => setAddFieldDraft((prev) => ({ ...prev, display_name: e.target.value }))}
                className="h-10 text-[13px]"
                placeholder="e.g. Company Size"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Description</Label>
              <Textarea
                value={addFieldDraft.description}
                onChange={(e) => setAddFieldDraft((prev) => ({ ...prev, description: e.target.value }))}
                className="min-h-[70px] text-[13px]"
                placeholder="Describe this attribute (optional)"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="text-[13px]" onClick={() => setAddFieldOpen(false)}>
              Cancel
            </Button>
            <Button
              className="text-[13px]"
              onClick={() => { handleSaveAddField().catch(() => {}); }}
              disabled={savingField || addFieldDraft.display_name.trim().length < 2}
            >
              {savingField ? "Creating..." : "Create attribute"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ObjectDetailView;
